import { LitElement, html, css, utils, styles } from '@hh/global.js'

class HHFormElementToggle extends LitElement {
  static properties = {
    formId: {
      type: String,
      attribute: 'form-id',
    },
    checked: {
      type: Boolean,
      reflect: true,
    },
    switchLabel: {
      type: String,
      attribute: 'switch-label',
    },
    switchLabelChecked: {
      type: String,
      attribute: 'switch-label-checked',
    },
    onChange: {
      type: String,
      attribute: 'custom:change',
    },
    inputEl: {
      type: Object,
    },
    mousedown: {
      reflect: true,
    },
    focused: {
      reflect: true,
    },
  }

  constructor() {
    super()
    this.inputEl = null
  }

  connectedCallback() {
    super.connectedCallback()
    this.inputEl = utils.one('[data-input]', this)
    this.inputEl.addEventListener(
      'input',
      utils
        .debounce((event) => {
          this.broadcastChange(event)
        }, 300)
        .bind(this)
    )
    this.addEventListener('keypress', this.handleKeyPress.bind(this), false)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('keypress', this.handleKeyPress.bind(this), false)
  }

  handleKeyPress(e) {
    if (e.key === 'Enter') {
      this.checked = !this.checked
      if (!this.inputEl) return

      this.broadcastChange()
      if (this.checked) {
        this.inputEl.setAttribute('form', this.formId)
      } else {
        this.inputEl.removeAttribute('form')
      }
    }
  }

  toggleChecked() {
    this.checked = !this.checked
    this.focused = false
    if (!this.inputEl) return

    this.broadcastChange()
    if (this.checked) {
      this.inputEl.setAttribute('form', this.formId)
      return
    }

    this.inputEl.removeAttribute('form')
  }

  broadcastChange(event) {
    this.dispatchEvent(
      new CustomEvent(this.onChange, {
        bubbles: true,
        composed: true,
        detail: {
          value: this.checked ? this.inputEl.value : '',
        },
      })
    )
  }

  onFocus(e) {
    this.focused = true
  }

  onFocusOut(e) {
    this.focused = false
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="switch-holder">
          <label class="switch">
            <input
              type="checkbox"
              class="visually-hidden"
              @change="${this.toggleChecked}"
              @mousedown="${this.onMouseDown}"
              @focusin="${this.onFocus}"
              @focusout="${this.onFocusOut}"
            />

            <span class="slider"></span>
          </label>

          <span>
            ${this.checked
              ? html`${this.switchLabel}`
              : html`${this.switchLabelChecked}`}
          </span>
        </div>

        <slot name="form-element" tabindex="${this.checked ? 0 : -1}"></slot>
      </div>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      .switch-holder {
        display: flex;
        align-items: center;
        gap: 0.6rem;
      }

      :host([focused='true']) .switch-holder:focus-within {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }

      :host([focused='true']) {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: 0;
      }

      :host([checked]) .switch-holder {
        margin-bottom: 2rem;
      }

      .switch {
        position: relative;
        display: inline-block;
        width: 3.9rem;
        height: 1.9rem;
      }

      .slider {
        position: absolute;
        cursor: pointer;
        inset: 0;
        background-color: var(--hh-color-brand-secondary);
        border-radius: 3.4rem;
        will-change: background-color;
        transition: background-color var(--hh-duration-default, 0.2s);
      }

      .slider::before {
        content: '';
        position: absolute;
        height: 1.4rem;
        width: 1.4rem;
        top: 0.25rem;
        left: 0.4rem;
        background-color: var(--hh-color-reverse);
        border-radius: 50%;
        will-change: transform;
        transition: transform var(--hh-duration-default, 0.2s);
      }

      :host([checked]) .slider {
        background-color: var(--hh-color-brand-tertiary);
      }

      :host([checked]) .slider:before {
        transform: translateX(1.9rem);
      }

      :host slot[name='form-element'] {
        display: none;
      }

      :host([checked]) slot[name='form-element'] {
        display: block;
      }
    `,
  ]
}

customElements.define('hh-form-element-toggle', HHFormElementToggle)
